import React, { useEffect, useState } from "react";
import { api } from "./api";

interface LarkStatus {
  connected: boolean;
  phase: "idle" | "authorizing" | "connected" | "error";
  screenshot?: string;
  viewport?: { width: number; height: number };
  error?: string;
}

export function LarkConnection({ onConnectionChange, loginUrl }: { onConnectionChange?: (connected: boolean) => void; loginUrl?: string }) {
  const [status, setStatus] = useState<LarkStatus>({ connected: false, phase: "idle" });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [text, setText] = useState("");
  const pending = status.phase === "authorizing";
  useEffect(() => { onConnectionChange?.(status.connected); }, [status.connected, onConnectionChange]);
  useEffect(() => {
    let stopped = false;
    let timer: number;
    const refresh = async () => {
      try {
        const result = await api<LarkStatus>("/api/lark/status");
        if (stopped) return;
        setStatus(result);
        if (result.phase === "authorizing") timer = window.setTimeout(() => void refresh(), 2000);
      } catch (reason) { if (!stopped) setError(reason instanceof Error ? reason.message : String(reason)); }
    };
    void refresh();
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [pending]);
  async function perform(path: string, body?: unknown) {
    setWorking(true); setError("");
    try { setStatus(await api<LarkStatus>(path, { method: "POST", ...(body === undefined ? {} : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }) })); }
    catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setWorking(false); }
  }
  async function type(key?: "Enter") {
    const value = text; setText("");
    await perform("/api/lark/action", { text: value, key });
  }
  return <section className="card"><div className="card-header"><div><h2>Lark 连接</h2><p className="hint">{status.connected ? "已登录, 登录状态自动保存" : pending ? "在下方窗口扫码或登录, 无需创建应用" : "登录后可导入多篇文档和图片"}</p></div><div className="row-actions"><span className={`badge ${status.connected ? "success" : ""}`}>{status.connected ? "已连接" : pending ? "等待登录" : "未连接"}</span><button className="btn btn-primary" disabled={working || pending} onClick={() => void perform("/api/lark/login", { url: loginUrl || undefined })}>{status.connected ? "检查登录" : "登录 Lark"}</button>{(status.connected || pending) && <button className="btn" disabled={working} onClick={() => void perform("/api/lark/logout")}>{pending ? "取消" : "断开"}</button>}</div></div>{pending && <div className="lark-authorization">{status.screenshot ? <img className="lark-browser" src={status.screenshot} alt="Lark 登录窗口" onClick={(event) => {
    if (working || !status.viewport) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    void perform("/api/lark/action", { click: { x: (event.clientX - bounds.left) * status.viewport.width / bounds.width, y: (event.clientY - bounds.top) * status.viewport.height / bounds.height } });
  }}/> : <p className="hint">正在打开登录窗口...</p>}<div className="lark-login-controls"><label className="field"><span>向登录窗口输入</span><input type="password" autoComplete="off" value={text} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void type("Enter"); } }}/></label><button className="btn" disabled={working || !text} onClick={() => void type()}>发送输入</button><button className="btn" disabled={working} onClick={() => void perform("/api/lark/action", { key: "Enter" })}>回车</button><button className="btn" disabled={working} onClick={() => void perform("/api/lark/action", { reload: true })}>刷新登录页</button></div><p className="hint">可点击窗口内的登录选项. 登录完成后, 状态自动更新.</p></div>}{(error || status.error) && <p className="error-text" role="alert">{error || status.error}</p>}</section>;
}
